import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    PhoneIncoming, PhoneOutgoing, RefreshCw, Loader2, Play, X, Search, FileText,
    ChevronLeft, ChevronRight, ListOrdered,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import { TELEPHONY_CALLS_URL, TELEPHONY_TRANSCRIBE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import RecordingPlayer from "../engage/ai-caller/RecordingPlayer";
import TranscriptView from "../../components/telephony/TranscriptView";
import {
    RECORDING_ENDPOINT, fmtDuration, canTranscribe, CallStatusBadge, TranscriptBadge, TranscribeButton,
} from "../../components/telephony/callMeta";
import { cn } from "../../utils/tailwindUtils";

const PAGE_SIZE = 50;

const DIRECTIONS = [["", "All directions"], ["inbound", "Inbound"], ["outbound", "Outbound"]];
const STATUSES = [["", "All statuses"], ["completed", "Completed"], ["missed", "Missed"], ["failed", "Failed"]];
// The API takes a comma-separated list; "none" means never transcribed.
const TRANSCRIPTS = [
    ["", "All transcripts"], ["done", "Transcribed"], ["queued,processing", "In progress"],
    ["failed", "Failed"], ["none", "Not transcribed"],
];

const selectCls = "h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground";

// An enquiry lives in Engage; a customer is a TrustEdge lead.
const leadHref = (c) => (c.enquiry_id ? `/engage/enquiries/${c.enquiry_id}`
    : c.customer_id ? `/customers/customerdetails?id=${c.customer_id}` : null);

export default function CallLog() {
    const { fetch, create } = useApiMethods();

    const [calls, setCalls] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [q, setQ] = useState("");
    const [direction, setDirection] = useState("");
    const [status, setStatus] = useState("");
    const [transcript, setTranscript] = useState("");

    const [selected, setSelected] = useState([]);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [playing, setPlaying] = useState(null);
    const [viewing, setViewing] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        fetch(TELEPHONY_CALLS_URL, {
            q, direction, status, transcript_status: transcript, limit: PAGE_SIZE, offset,
        })
            .then((res) => {
                if (res?.status !== "fail" && Array.isArray(res?.data)) {
                    setCalls(res.data);
                    setTotal(res.total ?? res.data.length);
                } else {
                    setCalls([]);
                    setTotal(0);
                    if (res?.message) ToastMsg("error", res.message);
                }
            })
            .catch(() => ToastMsg("error", "Failed to load calls"))
            .finally(() => setLoading(false));
    }, [q, direction, status, transcript, offset]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    // A new page or filter starts with nothing selected.
    useEffect(() => { setSelected([]); }, [q, direction, status, transcript, offset]);

    const setFilter = (setter) => (e) => { setter(e.target.value); setOffset(0); };
    const runSearch = (e) => { e?.preventDefault(); setQ(search.trim()); setOffset(0); };

    const eligible = calls.filter(canTranscribe);
    const allSelected = eligible.length > 0 && eligible.every((c) => selected.includes(c.id));
    const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    const toggleAll = () => setSelected(allSelected ? [] : eligible.map((c) => c.id));

    const transcribeSelected = () => {
        if (!selected.length) return;
        setBulkBusy(true);
        create(TELEPHONY_TRANSCRIBE_URL, { call_ids: selected })
            .then((res) => {
                ToastMsg(res?.status === "success" ? "success" : "error", res?.message || "Could not queue transcripts");
                if (res?.status === "success") { setSelected([]); load(); }
            })
            .catch((err) => ToastMsg("error", err?.response?.data?.message || "Could not queue transcripts"))
            .finally(() => setBulkBusy(false));
    };

    const from = total ? offset + 1 : 0;
    const to = Math.min(offset + PAGE_SIZE, total);

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                            <ListOrdered className="h-4 w-4" />
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-foreground">Call Log</h2>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Every staff call through Twilio or Smartflo, with recordings and transcripts.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {selected.length > 0 && (
                            <Button size="sm" onClick={transcribeSelected} disabled={bulkBusy}>
                                {bulkBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    : <FileText className="h-3.5 w-3.5 mr-1.5" />}
                                Transcribe selected ({selected.length})
                            </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2">
                    <form onSubmit={runSearch} className="flex items-center gap-1">
                        <Input value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Number or words in transcript"
                            className="h-9 w-64 text-sm" />
                        <Button type="submit" size="icon" variant="outline" className="h-9 w-9" title="Search">
                            <Search className="h-3.5 w-3.5" />
                        </Button>
                        {q && (
                            <Button type="button" size="icon" variant="ghost" className="h-9 w-9" title="Clear search"
                                onClick={() => { setSearch(""); setQ(""); setOffset(0); }}>
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </form>
                    <select value={direction} onChange={setFilter(setDirection)} className={selectCls}>
                        {DIRECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select value={status} onChange={setFilter(setStatus)} className={selectCls}>
                        {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select value={transcript} onChange={setFilter(setTranscript)} className={selectCls}>
                        {TRANSCRIPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </div>

                <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                            <tr className="text-muted-foreground">
                                <th className="px-3 py-2 w-8">
                                    <input type="checkbox" checked={allSelected} disabled={!eligible.length}
                                        onChange={toggleAll} title="Select every call that can be transcribed"
                                        className="rounded border-input" />
                                </th>
                                <th className="px-3 py-2 text-left font-medium">Time</th>
                                <th className="px-3 py-2 text-left font-medium">Number</th>
                                <th className="px-3 py-2 text-left font-medium">Lead</th>
                                <th className="px-3 py-2 text-left font-medium">Agent</th>
                                <th className="px-3 py-2 text-right font-medium">Duration</th>
                                <th className="px-3 py-2 text-left font-medium">Status</th>
                                <th className="px-3 py-2 text-left font-medium">Recording</th>
                                <th className="px-3 py-2 text-left font-medium">Transcript</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {calls.map((c) => {
                                const DirIcon = c.direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
                                const href = leadHref(c);
                                return [
                                    <tr key={c.id} className="hover:bg-accent/30">
                                        <td className="px-3 py-2">
                                            {canTranscribe(c) && (
                                                <input type="checkbox" checked={selected.includes(c.id)}
                                                    onChange={() => toggleOne(c.id)} className="rounded border-input" />
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{c.started_at || "—"}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1.5 text-foreground font-mono">
                                                <DirIcon className={cn("h-3.5 w-3.5",
                                                    c.direction === "inbound" ? "text-blue-500" : "text-green-500")} />
                                                {c.customer_number || "—"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 truncate max-w-[180px]">
                                            {href ? (
                                                <Link to={href} className="text-blue-600 dark:text-blue-400 hover:underline">
                                                    {c.lead_name || `#${c.enquiry_id || c.customer_id}`}
                                                </Link>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[140px]">{c.agent_name || "—"}</td>
                                        <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{fmtDuration(c.duration_seconds)}</td>
                                        <td className="px-3 py-2"><CallStatusBadge status={c.status} /></td>
                                        <td className="px-3 py-2">
                                            {c.has_recording ? (
                                                <button type="button" onClick={() => setPlaying(playing === c.id ? null : c.id)}
                                                    title={playing === c.id ? "Close recording" : "Play recording"}
                                                    className="p-1 rounded-full text-muted-foreground hover:text-teal-600 dark:hover:text-teal-400">
                                                    {playing === c.id ? <X className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                                                </button>
                                            ) : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            {c.transcript_status === "done" ? (
                                                <button type="button" onClick={() => setViewing(c)}
                                                    className="text-purple-600 dark:text-purple-400 hover:underline font-medium">
                                                    View
                                                </button>
                                            ) : canTranscribe(c) ? (
                                                <div className="flex items-center gap-1.5">
                                                    <TranscriptBadge status={c.transcript_status} />
                                                    <TranscribeButton callId={c.id} onQueued={load} />
                                                </div>
                                            ) : (
                                                <TranscriptBadge status={c.transcript_status} />
                                            )}
                                        </td>
                                    </tr>,
                                    playing === c.id && (
                                        <tr key={`${c.id}-audio`}>
                                            <td colSpan={9} className="px-3 pb-2">
                                                <RecordingPlayer callId={c.id} endpoint={RECORDING_ENDPOINT} variant="expanded" />
                                            </td>
                                        </tr>
                                    ),
                                ];
                            })}
                            {!calls.length && (
                                <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                                    {loading ? "Loading…" : "No calls match these filters."}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{total ? `${from}–${to} of ${total}` : "0 calls"}</span>
                    <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" disabled={offset === 0 || loading}
                            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                            <ChevronLeft className="h-3.5 w-3.5" /> Prev
                        </Button>
                        <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= total || loading}
                            onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                            Next <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                </div>
            </div>

            <DialogModal open={!!viewing} onClose={() => setViewing(null)} showActions={false}
                width="640px"
                title={viewing ? `Transcript — ${viewing.lead_name || viewing.customer_number || `call #${viewing.id}`}` : ""}
                subtitle={viewing ? `${viewing.started_at || ""} · ${fmtDuration(viewing.duration_seconds)} · ${viewing.agent_name || "—"}` : ""}>
                {viewing && <TranscriptView callId={viewing.id} />}
            </DialogModal>
        </div>
    );
}
